import { useState, useEffect, useCallback } from "react";
import { Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  ExamSession,
  CreateExamSessionPayload,
  UpdateExamSessionPayload,
} from "../../../../types/markTypes";
import { ClassData } from "../../../../types/classTypes";
import {
  getExamSessions,
  createExamSession,
  updateExamSession,
  deleteExamSession,
  getTeacherExamSessions,
  publishMarksByExamSession,
} from "../../../../services/markService";
import { getClassesBySubject } from "../../../../services/subjectService";
import { getClassLevels } from "services/feeTypeService";
import { ExamType } from "types/examTypeTypes";
import { getExamTypesWithParams } from "services/examTypeServices";

export const useExamSessions = () => {
  const { t } = useTranslation();
  const [examSessions, setExamSessions] = useState<ExamSession[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [isClassesLoading, setIsClassesLoading] = useState<boolean>(false);
  const [classesError, setClassesError] = useState<Error | null>(null);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [classLevels, setClassLevels] = useState<any[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[] | null | undefined>(null);
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });

  const fetchExamSessions = useCallback(async (filter: any) => {
    setIsLoading(true);
    try {
      const response = await getExamSessions(filter);
      setExamSessions(response.data);
      setPagination(response.pagination)
    } catch (err) {
      setError(err as Error);
      message.error(t("fetch_exam_sessions_error"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchTeacherExamSessions = useCallback(
    async (teacherId: string) => {
      setIsLoading(true);
      try {
        const response = await getTeacherExamSessions(teacherId, { page: 1, limit: 10000 });
        setExamSessions(response.data);
      } catch (err) {
        setError(err as Error);
        message.error(t("fetch_teacher_exam_sessions_error"));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const fetchClassesBySubject = useCallback(
    async (subjectId: string) => {
      setIsClassesLoading(true);
      try {
        const response = await getClassesBySubject(subjectId);
        setClasses(response);
      } catch (err) {
        setClassesError(err as Error);
        message.error(t("fetch_classes_error"));
      } finally {
        setIsClassesLoading(false);
      }
    },
    [t]
  );

  const handleCreateExamSession = useCallback(
    async (payload: CreateExamSessionPayload) => {
      setIsSubmitting(true);
      try {
        await createExamSession(payload);
        // await fetchExamSessions({ page: pagination.page, limit: pagination.limit });
        message.success(t("exam_session_created"));
        return true;
      } catch (err) {
        setError(err as Error);
        return false
        // message.error(t("exam_session_create_error"));
      } finally {
        setIsSubmitting(false);
      }
    },
    [t]
  );

  const handleUpdateExamSession = useCallback(
    async (payload: UpdateExamSessionPayload) => {
      setIsSubmitting(true);
      try {
        await updateExamSession(payload);
        message.success(t("exam_session_updated"));
        // await fetchExamSessions({ page: pagination.page, limit: pagination.limit });
        return true;
      } catch (err) {
        // message.error(t("exam_session_update_error"));
        return false
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchExamSessions, t]
  );


  const handleDeleteExamSession = async (expenseId: string) => {
    try {
      await deleteExamSession(expenseId);
      message.success(t("exam_session_deleted"));
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("exam_session_delete_error"));
      return false;
    }
  };

  const handlePublishMarks = useCallback(
    async (examSessionId: string) => {
      setIsPublishing(true);
      try {
        await publishMarksByExamSession(examSessionId);
        message.success(t("marks_published"));
        setExamSessions((prev) =>
          prev.map((session) =>
            session.id === examSessionId
              ? {
                ...session,
                classes: session.classes?.map((cls) => ({
                  ...cls,
                  submissionStatus: "published",
                })),
              }
              : session
          )
        );
      } catch (error) {
        message.error(t("marks_publish_error"));
      } finally {
        setIsPublishing(false);
      }
    },
    [t]
  );

  const handleGetClassLevels = async () => {
    try {
      const response = await getClassLevels();
      setClassLevels(response.data);
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const getExamTypes = async (params: any) => {
    try {
      const response = await getExamTypesWithParams(params);
      if (response) {
        setExamTypes(response.data);
      }
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // useEffect(() => {
  //   fetchExamSessions({ page: pagination.page, limit: pagination.limit });
  // }, [fetchExamSessions, pagination]);

  return {
    examSessions,
    classes,
    isLoading,
    isClassesLoading,
    isSubmitting,
    isPublishing,
    error,
    classesError,
    fetchExamSessions,
    fetchTeacherExamSessions,
    fetchClassesBySubject,
    handleCreateExamSession,
    handleUpdateExamSession,
    handleDeleteExamSession,
    handlePublishMarks,
    classLevels,
    handleGetClassLevels,
    examTypes,
    getExamTypes,
    pagination,
  };
};
